/**
 * The wire shapes `Rws.AiInsights.Host.Settings.PortalSettingsAdminEndpoints` returns for the request
 * types tab of System Settings (`AIA-81`): whether each kind of request may be approved without an
 * approver. Gated on `settings.write`.
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** One request type and whether a self-raised request of that type is approved as soon as it is raised. */
export interface RequestTypeAutoApproval {
  requestType: string;
  /** False for a type that is carried out by hand; its switch is shown but cannot be turned on. */
  canBeAutoApproved: boolean;
  autoApprove: boolean;
  /** When the setting was last changed, or null when it has never been changed. */
  updatedAt: string | null;
  /** Who a new request of this type is routed to. */
  assigneeKind: AssigneeKind;
  /** The role a request is routed to when `assigneeKind` is `Role`; otherwise null. */
  assigneeRoleId: string | null;
  /** The name of that role. */
  assigneeRoleName: string | null;
}

/** Who a new request of a type is routed to: nobody, the owner of the requester's budget group, or the members of a role. */
export type AssigneeKind = "Unassigned" | "GroupOwner" | "Role";

/** A role a request type can be routed to: one that carries `request.approve`, so its members can decide what is routed to it. */
export interface AssignableRole {
  roleId: string;
  name: string;
}

const requestTypesPath = "/api/entitlement/settings/request-types";

/** Calls `GET /api/entitlement/settings/request-types`. */
export async function fetchRequestTypeAutoApprovals(): Promise<AdminResult<RequestTypeAutoApproval[]>> {
  return adminRequest<RequestTypeAutoApproval[]>(requestTypesPath, { method: "GET" }, "request types");
}

/** Calls `PUT /api/entitlement/settings/request-types/{requestType}`. */
export async function updateRequestTypeAutoApproval(requestType: string, autoApprove: boolean): Promise<AdminResult<RequestTypeAutoApproval>> {
  return adminRequest<RequestTypeAutoApproval>(
    `${requestTypesPath}/${encodeURIComponent(requestType)}`,
    { method: "PUT", body: JSON.stringify({ autoApprove }) },
    "request type update",
  );
}

/** Calls `GET /api/entitlement/settings/request-types/assignable-roles`. */
export async function fetchAssignableRoles(): Promise<AdminResult<AssignableRole[]>> {
  return adminRequest<AssignableRole[]>(`${requestTypesPath}/assignable-roles`, { method: "GET" }, "assignable roles");
}

/** Calls `PUT /api/entitlement/settings/request-types/{requestType}/assignee`. */
export async function updateRequestTypeAssignee(requestType: string, kind: AssigneeKind, roleId: string | null): Promise<AdminResult<RequestTypeAutoApproval>> {
  return adminRequest<RequestTypeAutoApproval>(
    `${requestTypesPath}/${encodeURIComponent(requestType)}/assignee`,
    { method: "PUT", body: JSON.stringify({ kind, roleId }) },
    "request type assignee",
  );
}
